import { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FileText, ExternalLink, Pencil, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest } from "@/lib/queryClient";

interface EligibleArtifact {
  id: string;
  title: string;
  sourceType: string;
  updatedAt: string;
}

function artifactTypeLabel(sourceType: string): string {
  if (sourceType === "chat") return "Chat Capture";
  return "Note";
}

interface ArtifactPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (id: string) => void;
}

function ArtifactPickerDialog({ open, onOpenChange, onSelect }: ArtifactPickerDialogProps) {
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery<{ artifacts: EligibleArtifact[] }>({
    queryKey: ["/api/user-profile/eligible-artifacts"],
    enabled: open,
  });

  const filtered = (data?.artifacts ?? []).filter((a) =>
    a.title.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="artifact-picker-dialog">
        <DialogHeader>
          <DialogTitle>Choose a hero artifact</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search notes..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
            data-testid="artifact-search-input"
          />
        </div>
        <div className="max-h-60 overflow-y-auto space-y-1">
          {isLoading && (
            <div className="space-y-2 py-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          )}
          {!isLoading && filtered.length === 0 && (
            <p className="py-4 text-center text-sm text-muted-foreground">
              No notes found
            </p>
          )}
          {filtered.map((artifact) => (
            <button
              key={artifact.id}
              className="w-full text-left rounded-md px-3 py-2 text-sm hover:bg-muted transition-colors"
              data-testid={`artifact-option-${artifact.id}`}
              onClick={() => {
                onSelect(artifact.id);
                onOpenChange(false);
              }}
            >
              <div className="font-medium truncate">{artifact.title}</div>
              <div className="text-xs text-muted-foreground">
                {artifactTypeLabel(artifact.sourceType)} ·{" "}
                {formatDistanceToNow(new Date(artifact.updatedAt), { addSuffix: true })}
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface HeroArtifactCardProps {
  artifactId: string | null | undefined;
  onUpdate: (id: string | null) => void;
}

export function HeroArtifactCard({ artifactId, onUpdate }: HeroArtifactCardProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data: note, isLoading, isError } = useQuery<any>({
    queryKey: ["/api/notes", artifactId],
    enabled: !!artifactId,
  });

  const mutation = useMutation({
    mutationFn: (id: string | null) =>
      apiRequest("PATCH", "/api/user-profile", { heroArtifactId: id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["personalised-profile"] });
      queryClient.invalidateQueries({ queryKey: ["/api/user-profile/eligible-artifacts"] });
    },
  });

  function handleSelect(id: string) {
    onUpdate(id);
    mutation.mutate(id);
  }

  function handleClear() {
    onUpdate(null);
    mutation.mutate(null);
  }

  if (!artifactId) {
    return (
      <>
        <Card className="border-dashed" data-testid="hero-artifact-empty">
          <CardContent className="flex flex-col items-center justify-center py-6 gap-2">
            <FileText className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground text-center">
              Pick an artifact to feature here
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPickerOpen(true)}
              data-testid="choose-artifact-btn"
            >
              Choose
            </Button>
          </CardContent>
        </Card>
        <ArtifactPickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onSelect={handleSelect}
        />
      </>
    );
  }

  if (isLoading) {
    return (
      <Card data-testid="hero-artifact-loading">
        <CardContent className="py-4 space-y-2">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-3 w-32" />
        </CardContent>
      </Card>
    );
  }

  if (isError || !note) {
    return (
      <>
        <Card className="border-dashed" data-testid="hero-artifact-error">
          <CardContent className="flex flex-col items-center justify-center py-6 gap-2">
            <FileText className="h-8 w-8 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground text-center">
              Artifact not found
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPickerOpen(true)}
              data-testid="choose-artifact-btn"
            >
              Choose another
            </Button>
          </CardContent>
        </Card>
        <ArtifactPickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onSelect={handleSelect}
        />
      </>
    );
  }

  return (
    <>
      <Card data-testid="hero-artifact-card">
        <CardContent className="py-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground mb-0.5">
                {artifactTypeLabel(note.sourceType || "manual")}
              </p>
              <p className="font-semibold truncate" data-testid="artifact-title">
                {note.title || "Untitled note"}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Updated{" "}
                {formatDistanceToNow(new Date(note.updatedAt || note.createdAt), {
                  addSuffix: true,
                })}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                variant="outline"
                size="sm"
                asChild
                data-testid="open-artifact-btn"
              >
                <a href="/notes">
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                  Open
                </a>
              </Button>
            </div>
          </div>
          <div className="mt-2">
            <button
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => setPickerOpen(true)}
              data-testid="change-artifact-btn"
            >
              Change
            </button>
            <span className="mx-1.5 text-muted-foreground/40 text-xs">·</span>
            <button
              className="text-xs text-muted-foreground hover:text-destructive transition-colors"
              onClick={handleClear}
              data-testid="clear-artifact-btn"
            >
              Remove
            </button>
          </div>
        </CardContent>
      </Card>
      <ArtifactPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={handleSelect}
      />
    </>
  );
}
